import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { PageHeader, Panel, Stat } from "@/components/Layout";
import { api, formatError, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const C = { claimed: "#059669", pending: "#f59e0b", returned: "#ef4444", rate: "#0f172a" };

const Chart = ({ series }) => (
  <div className="h-72" data-testid="insights-chart">
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
        <XAxis dataKey="week" tick={{ fontSize: 11 }} tickFormatter={(w) => w.slice(5)} />
        <YAxis yAxisId="n" tick={{ fontSize: 11 }} allowDecimals={false} />
        <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar yAxisId="n" dataKey="claimed" stackId="a" fill={C.claimed} name="Claimed" />
        <Bar yAxisId="n" dataKey="pending" stackId="a" fill={C.pending} name="Pending" />
        <Bar yAxisId="n" dataKey="returned" stackId="a" fill={C.returned} name="Returned" radius={[4, 4, 0, 0]} />
        <Line yAxisId="r" dataKey="claim_rate" stroke={C.rate} strokeWidth={2} dot={false} name="Claim rate %" />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
);

const BatchBars = ({ batches }) => (
  <div className="space-y-3" data-testid="insights-batches">
    {!batches.length && <p className="text-sm text-slate-500">No batches yet.</p>}
    {batches.map((b) => {
      const total = b.count || 1;
      return (
        <Link key={b.id} to={`/app/bulk/${b.id}`} data-testid={`insights-batch-${b.reference}`} className="block group">
          <div className="flex justify-between text-sm"><span className="font-medium group-hover:underline">{b.name} <span className="font-mono text-xs text-slate-500">{b.reference}</span></span>
            <span className="text-xs text-slate-500">{b.claimed} claimed · {b.pending} pending · {b.returned} returned</span></div>
          <div className="mt-1.5 h-2.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden flex">
            {[["claimed", C.claimed], ["pending", C.pending], ["returned", C.returned]].map(([k, c]) => <div key={k} style={{ width: `${(b[k] / total) * 100}%`, background: c }} />)}
          </div>
        </Link>
      );
    })}
  </div>
);

const ChaseButton = ({ m, onDone }) => {
  const [busy, setBusy] = useState(false);
  const left = 3 - (m.resend_count || 0);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/mail/${m.id}/resend-notification`);
      toast.success(`Notice resent to ${m.recipient_name}`);
      onDone();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <button data-testid={`chase-btn-${m.reference}`} onClick={go} disabled={busy || left <= 0} className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-slate-300 dark:border-slate-700 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50 whitespace-nowrap">
      {busy ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />} Resend notice ({left})
    </button>
  );
};

const Pending = ({ rows, canChase, onDone }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="insights-pending-table">
      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200 dark:border-slate-800">
        {["Days left", "Recipient", "Letter", "Batch", "Last notified", ""].map((h) => <th key={h} className="px-3 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody>{rows.map((m) => (
        <tr key={m.id} className="border-b last:border-0 border-slate-100 dark:border-slate-800">
          <td className="px-3 py-3"><span className={`font-mono font-semibold ${m.days_left <= 3 ? "text-red-600" : m.days_left <= 7 ? "text-amber-600" : ""}`}>{m.days_left}</span></td>
          <td className="px-3 py-3 whitespace-nowrap">{m.recipient_name} <span className="text-xs text-slate-500">· {m.recipient_email}</span></td>
          <td className="px-3 py-3"><Link to={`/app/mail/${m.id}`} className="font-mono text-xs underline">{m.reference}</Link></td>
          <td className="px-3 py-3 text-xs">{m.batch_name || "—"}</td>
          <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">{fmtDate(m.last_resent_at || m.notified_at)}</td>
          <td className="px-3 py-3 text-right">{canChase && <ChaseButton m={m} onDone={onDone} />}</td>
        </tr>))}</tbody>
    </table>
    {!rows.length && <p className="text-sm text-slate-500 p-6 text-center" data-testid="insights-no-pending">Nobody to chase. Every letter has been claimed or returned.</p>}
  </div>
);

export default function Insights() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [weeks, setWeeks] = useState(12);
  const load = () => api.get("/insights", { params: { weeks } }).then((r) => setD(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => {
    if (!user?.enterprise) return undefined;
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [weeks, user?.enterprise]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!user?.enterprise) return <div data-testid="insights-locked" className="max-w-xl"><PageHeader eyebrow="Enterprise" title="Insights" /><Panel><p className="flex gap-2 text-sm"><Lock size={16} className="text-emerald-600" /> Insights are available to Enterprise teams.</p></Panel></div>;
  if (!d) return <Loader2 className="animate-spin text-emerald-600" />;
  const t = d.totals;
  return (
    <div data-testid="insights-page" className="space-y-6 min-w-0">
      <PageHeader eyebrow={user.org?.name} title="Insights">
        <select data-testid="insights-weeks-select" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className="h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm">
          {[4, 12, 26, 52].map((w) => <option key={w} value={w}>Last {w} weeks</option>)}
        </select>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat testId="insights-claim-rate" label="Claim rate" value={`${t.claim_rate}%`} accent />
        <Stat testId="insights-sent" label="Sent" value={t.sent} />
        <Stat testId="insights-claimed" label="Claimed" value={t.claimed} />
        <Stat testId="insights-pending" label="Pending" value={t.pending} />
        <Stat testId="insights-returned" label="Returned" value={t.returned} />
      </div>
      <Panel title="Claims over time (by week sent)" className="min-w-0"><Chart series={d.series} /><p className="text-[11px] text-slate-500 mt-2">Refreshes every minute.</p></Panel>
      <div className="grid xl:grid-cols-5 gap-6 min-w-0">
        <Panel title="By batch" className="xl:col-span-2 min-w-0"><BatchBars batches={d.batches} /></Panel>
        <Panel title={`Recipients to chase (${d.pending.length})`} className="xl:col-span-3 min-w-0"><Pending rows={d.pending} canChase={d.can_chase} onDone={load} /></Panel>
      </div>
    </div>
  );
}
